import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import '../styles/Forms.css';
import countries from '../data/countries';

const LinealForm = ({ onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState({
    // Form type identifier
    formType: 'lineal',
    
    // Primary Applicant Information
    fullName: '',
    streetAddress: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: 'United States',
    applicantDateOfBirth: '',
    applicantPlaceOfBirth: '',
    telephone: '',
    email: '',
    generation: 'First Generation',
    
    // Qualifying Veteran Information
    veteranName: '',
    dateOfBirth: '',
    placeOfBirth: '',
    dateOfDeath: '',
    placeOfDeath: '',
    datesOfService: '',
    enlistmentDate: '',
    enlistmentLocation: '',
    serviceNumber: '',
    branchOfService: 'Select',
    division: '',
    regiment: '',
    battalion: '',
    company: '',
    rank: '',
    theater: 'Select',
    battles: '',
    awards: '',
    additionalInfo: '',
    
    // Branch-specific fields
    // Navy & Coast Guard
    shipName: '',
    fleetAssignment: '',
    battleGroup: '',
    
    // USAAF
    aircraftType: '',
    squadron: '',
    group: '',
    wing: '',
    numberedAirForce: '',
    crewPosition: '',
    
    // Merchant Marine
    convoyAssignment: '',
    
    // Marriage information
    marriageStatus: 'Select',
    marriageDate: '',
    marriagePlace: '',
    spouseName: '',
    spouseBirthDate: '',
    spouseBirthPlace: '',
    spouseDeathDate: '',
    spouseDeathPlace: '',
    
    // Supporting Documentation
    documentFiles: {
      dd214: [],
      militaryRecords: [],
      vaPaperwork: [],
      pensionRecords: [],
      birthCertificates: [],
      marriageRecords: [],
      veteranPhoto: [],
      miscellaneous: []
    },
    promoCode: '',
    
    // Consent and acknowledgment
    acknowledgment: false,
    marketingOptIn: false,
    
    // How did you hear about us
    howDidYouHearAboutUs: ''
  });

  const [firstGeneration, setFirstGeneration] = useState([{ id: Date.now(), name: '', dateOfBirth: '', placeOfBirth: '', spouseName: '', dateOfDeath: '', placeOfDeath: '' }]);
  const [secondGeneration, setSecondGeneration] = useState([]);
  const [thirdGeneration, setThirdGeneration] = useState([]);
  const [fourthGeneration, setFourthGeneration] = useState([]);
  const [supplementalMembers, setSupplementalMembers] = useState([]);
  const [promoDiscount, setPromoDiscount] = useState({ type: 'percentage', value: 0 });
  const [totalFileSize, setTotalFileSize] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaRef = useRef(null);
  
  // Updated file size limits for S3 direct uploads
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file (was 50MB)
  const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100MB total (was 200MB)
  
  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // Calculate total file size
  const calculateTotalFileSize = (files) => {
    let total = 0;
    Object.values(files).forEach(fileArray => {
      fileArray.forEach(file => {
        total += file.size || 0;
      });
    });
    return total;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'file') {
      // File handling is already implemented
    } else if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });

      // COMMENTED OUT: Generation handling logic since lineal descendant section is disabled
      // if (name === 'generation') {
      //   if (value === 'First Generation') {
      //     // Reset all other generations
      //     setSecondGeneration([]);
      //     setThirdGeneration([]);
      //     setFourthGeneration([]);
      //   } else if (value === 'Second Generation') {
      //     // Reset third and fourth generation
      //     setThirdGeneration([]);
      //     setFourthGeneration([]);
      //   } else if (value === 'Third Generation') {
      //     // Reset fourth generation
      //     setFourthGeneration([]);
      //   }
      // }

      // Check if promo code is valid
      if (name === 'promoCode') {
        if (value.toUpperCase() ==='TEAM') {
          setPromoDiscount({ type: 'mainApplication', value: 1.0 }); // 100% discount on main application only
        } else if (value.toUpperCase() === 'MEMBER') {
          setPromoDiscount({ type: 'fixed', value: 80 }); // $80 flat discount
        } else if (value.toUpperCase() === 'THC') {
          setPromoDiscount({ type: 'fixed', value: 40 }); // $40 flat discount
        } else if (value.toUpperCase() === 'MULTIPLE') {
          setPromoDiscount({ type: 'fixed', value: 125 }); // $125 flat discount
        } else if (value.toUpperCase() === 'BLUESTAR') {
          // Check if current date is before September 1st
          const currentDate = new Date();
          const currentYear = currentDate.getFullYear();
          const laborDay = new Date(currentYear, 8, 1); // September 1st (month is 0-indexed)
          
          if (currentDate <= laborDay) {
          setPromoDiscount({ type: 'fixed', value: 80 }); // $80 flat discount
          } else {
            setPromoDiscount({ type: 'expired', value: 0 });
            alert('The BLUESTAR promo code expired on Labor Day (September 1st).');
          }
        } else {
          setPromoDiscount({ type: 'percentage', value: 0 });
        }
      }
    }
  };

  const handleFileChange = (e) => {
    const { id, files } = e.target;
    
    // Convert FileList to Array
    const filesArray = Array.from(files);
    
    // Check each file size
    const oversizedFiles = filesArray.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      alert(`The following files exceed the 10MB limit:\n${oversizedFiles.map(f => `${f.name} (${formatFileSize(f.size)})`).join('\n')}`);
      return;
    }
    
    // Check total size after adding these files
    const newTotalSize = calculateTotalFileSize({
      ...formData.documentFiles,
      [id]: [...formData.documentFiles[id], ...filesArray]
    });
    
    if (newTotalSize > MAX_TOTAL_SIZE) {
      alert(`Adding these files would exceed the 100MB total limit.\nCurrent: ${formatFileSize(totalFileSize)}\nAfter adding: ${formatFileSize(newTotalSize)}`);
      return;
    }
    
    // If all checks pass, update the files
    const updatedFiles = {
      ...formData.documentFiles,
      [id]: [...formData.documentFiles[id], ...filesArray]
    };
    
    setFormData(prevData => ({
      ...prevData,
      documentFiles: updatedFiles
    }));
    
    // Update total file size
    setTotalFileSize(newTotalSize);
  };
  
  const removeFile = (documentType, fileIndex) => {
    const fileToRemove = formData.documentFiles[documentType][fileIndex];
    const fileSize = fileToRemove.size || 0;
    
    setFormData(prevData => ({
      ...prevData,
      documentFiles: {
        ...prevData.documentFiles,
        [documentType]: prevData.documentFiles[documentType].filter((_, index) => index !== fileIndex)
      }
    }));
    
    // Update total file size
    setTotalFileSize(prevSize => prevSize - fileSize);
  };

  // Function to get file names for display
  const getFileList = (documentType) => {
    return formData.documentFiles[documentType] || [];
  };

  const handleCheckboxChange = (e, category) => {
    const { value, checked } = e.target;
    
    if (checked) {
      setFormData({
        ...formData,
        [category]: [...formData[category], value]
      });
    } else {
      setFormData({
        ...formData,
        [category]: formData[category].filter(item => item !== value)
      });
    }
  };

  const addLinealDescendant = (generation) => {
    if (generation === 'first' && firstGeneration.length < 4) {
      setFirstGeneration([
        ...firstGeneration,
        {
          id: Date.now(),
          name: '',
          dateOfBirth: '',
          placeOfBirth: '',
          spouseName: '',
          dateOfDeath: '',
          placeOfDeath: ''
        }
      ]);
    } else if (generation === 'second' && secondGeneration.length < 4) {
      setSecondGeneration([
        ...secondGeneration,
        {
          id: Date.now(),
          name: '',
          dateOfBirth: '',
          placeOfBirth: '',
          spouseName: '',
          dateOfDeath: '',
          placeOfDeath: ''
        }
      ]);
    } else if (generation === 'third' && thirdGeneration.length < 4) {
      setThirdGeneration([
        ...thirdGeneration,
        {
          id: Date.now(),
          name: '',
          dateOfBirth: '',
          placeOfBirth: '',
          spouseName: '',
          dateOfDeath: '',
          placeOfDeath: ''
        }
      ]);
    } else if (generation === 'fourth' && fourthGeneration.length < 4) {
      setFourthGeneration([
        ...fourthGeneration,
        {
          id: Date.now(),
          name: '',
          dateOfBirth: '',
          placeOfBirth: '',
          spouseName: '',
          dateOfDeath: '',
          placeOfDeath: ''
        }
      ]);
    }
  };

  const updateLinealDescendant = (generation, id, field, value) => {
    if (generation === 'first') {
      setFirstGeneration(
        firstGeneration.map(descendant => 
          descendant.id === id ? { ...descendant, [field]: value } : descendant
        )
      );
    } else if (generation === 'second') {
      setSecondGeneration(
        secondGeneration.map(descendant => 
          descendant.id === id ? { ...descendant, [field]: value } : descendant
        )
      );
    } else if (generation === 'third') {
      setThirdGeneration(
        thirdGeneration.map(descendant => 
          descendant.id === id ? { ...descendant, [field]: value } : descendant
        )
      );
    } else if (generation === 'fourth') {
      setFourthGeneration(
        fourthGeneration.map(descendant => 
          descendant.id === id ? { ...descendant, [field]: value } : descendant
        )
      );
    }
  };

  const addSupplementalMember = () => {
    if (supplementalMembers.length < 20) {
      setSupplementalMembers([
        ...supplementalMembers,
        {
          id: Date.now(),
          name: '',
          dateOfBirth: '',
          streetAddress: '',
          city: '',
          stateProvince: '',
          postalCode: '',
          country: 'United States',
          telephone: '',
          email: '',
          relationship: ''
        }
      ]);
    }
  };

  const updateSupplementalMember = (id, field, value) => {
    setSupplementalMembers(
      supplementalMembers.map(member => 
        member.id === id ? { ...member, [field]: value } : member
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset validation errors
    setValidationErrors({});
    
    // Validate email
    if (!validateEmail(formData.email)) {
      setValidationErrors(prev => ({
        ...prev,
        email: 'Please enter a valid email address'
      }));
      document.getElementById('email').focus();
      return;
    }
    
    // Validate dropdown selections
    if (formData.branchOfService === 'Select') {
      setValidationErrors(prev => ({
        ...prev,
        branchOfService: 'Please select a branch of service'
      }));
      document.getElementById('branchOfService').focus();
      return;
    }
    
    // Theater is no longer required, remove validation
    
    if (formData.marriageStatus === 'Select') {
      setValidationErrors(prev => ({
        ...prev,
        marriageStatus: 'Please select a marriage status'
      }));
      document.getElementById('marriageStatus').focus();
      return;
    }
    
    // Check acknowledgment
    if (!formData.acknowledgment) {
      setValidationErrors(prev => ({
        ...prev,
        acknowledgment: 'You must acknowledge and consent to the terms before submitting'
      }));
      document.getElementById('acknowledgment').focus();
      return;
    }
    
    // Check if at least one proof of service document is attached
    const hasProofOfService = 
      formData.documentFiles.dd214.length > 0 || 
      formData.documentFiles.militaryRecords.length > 0 || 
      formData.documentFiles.vaPaperwork.length > 0 || 
      formData.documentFiles.pensionRecords.length > 0;
    
    if (!hasProofOfService) {
      setValidationErrors(prev => ({
        ...prev,
        proofOfService: 'Please upload at least one document as proof of veteran\'s service'
      }));
      // Scroll to documentation section
      document.querySelector('.document-section').scrollIntoView({ behavior: 'smooth' });
      return;
    }
    
    // Check if at least one proof of lineal descent document is attached
    const hasProofOfDescent = 
      formData.documentFiles.birthCertificates.length > 0 || 
      formData.documentFiles.marriageRecords.length > 0;
    
    if (!hasProofOfDescent) {
      setValidationErrors(prev => ({
        ...prev,
        proofOfDescent: 'Please upload at least one document as proof of lineal descent'
      }));
      // Scroll to lineal descent documentation section
      document.querySelectorAll('.document-section')[1].scrollIntoView({ behavior: 'smooth' });
      return;
    }
    
    // Validate supplemental member emails if any
    let supplementalEmailsValid = true;
    supplementalMembers.forEach((member, index) => {
      if (member.email && !validateEmail(member.email)) {
        setValidationErrors(prev => ({
          ...prev,
          [`supplementalEmail${index}`]: 'Please enter a valid email address'
        }));
        supplementalEmailsValid = false;
      }
    });
    
    if (!supplementalEmailsValid) {
      return;
    }
    
    // Execute reCAPTCHA verification
    try {
      await recaptchaRef.current.execute();
    } catch (error) {
      console.error('reCAPTCHA execution error:', error);
      setValidationErrors(prev => ({
        ...prev,
        recaptcha: 'reCAPTCHA verification failed. Please try again.'
      }));
      return;
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const renderBranchSpecificFields = () => {
    switch (formData.branchOfService) {
      case 'United States Army (USA)':
        return (
          <>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="division">Division:</label>
                  <input 
                    type="text" 
                    id="division" 
                    name="division"
                    className="form-control"
                    value={formData.division}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="regiment">Regiment/Group:</label>
                  <input 
                    type="text" 
                    id="regiment" 
                    name="regiment"
                    className="form-control"
                    value={formData.regiment}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="battalion">Battalion/Squadron:</label>
                  <input 
                    type="text" 
                    id="battalion" 
                    name="battalion"
                    className="form-control"
                    value={formData.battalion}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="company">Company/Battery/Troop:</label>
                  <input 
                    type="text" 
                    id="company" 
                    name="company"
                    className="form-control"
                    value={formData.company}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </>
        );
      case 'United States Navy (USN)':
        return (
          <>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="shipName">Ship/Submarine Name & Hull Number:</label>
                  <input 
                    type="text" 
                    id="shipName" 
                    name="shipName"
                    className="form-control"
                    value={formData.shipName}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="fleetAssignment">Fleet Assignment:</label>
                  <input 
                    type="text" 
                    id="fleetAssignment" 
                    name="fleetAssignment"
                    className="form-control"
                    value={formData.fleetAssignment}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="division">Division:</label>
                  <input 
                    type="text" 
                    id="division" 
                    name="division"
                    className="form-control"
                    value={formData.division}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="battleGroup">Battle Group/Task Force:</label>
                  <input 
                    type="text" 
                    id="battleGroup" 
                    name="battleGroup"
                    className="form-control"
                    value={formData.battleGroup}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </>
        );
      case 'United States Marine Corps (USMC)':
        return (
          <>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="division">Division:</label>
                  <input 
                    type="text" 
                    id="division" 
                    name="division"
                    className="form-control"
                    value={formData.division}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="regiment">Regiment:</label>
                  <input 
                    type="text" 
                    id="regiment" 
                    name="regiment"
                    className="form-control"
                    value={formData.regiment}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="battalion">Battalion:</label>
                  <input 
                    type="text" 
                    id="battalion" 
                    name="battalion"
                    className="form-control"
                    value={formData.battalion}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="company">Company/Battery:</label>
                  <input 
                    type="text" 
                    id="company" 
                    name="company"
                    className="form-control"
                    value={formData.company}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </>
        );
      case 'United States Coast Guard (USCG)':
        return (
          <div className="form-row">
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="shipName">Ship Name & Hull Number:</label>
                <input 
                  type="text" 
                  id="shipName" 
                  name="shipName"
                  className="form-control"
                  value={formData.shipName}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>
        );
      case 'United States Merchant Marine (USMM)':
        return (
          <>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="shipName">Ship Name & Hull Number:</label>
                  <input 
                    type="text" 
                    id="shipName" 
                    name="shipName"
                    className="form-control"
                    value={formData.shipName}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="convoyAssignment">Convoy Assignment:</label>
                  <input 
                    type="text" 
                    id="convoyAssignment" 
                    name="convoyAssignment"
                    className="form-control"
                    value={formData.convoyAssignment}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="rank">Rank/Rating:</label>
                  <input 
                    type="text" 
                    id="rank" 
                    name="rank"
                    className="form-control"
                    value={formData.rank}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="fleetAssignment">Fleet Assignment:</label>
                  <input 
                    type="text" 
                    id="fleetAssignment" 
                    name="fleetAssignment"
                    className="form-control"
                    value={formData.fleetAssignment}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </>
        );
      case 'United States Army Air Forces (USAAF)':
        return (
          <>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="aircraftType">Aircraft Type:</label>
                  <input 
                    type="text" 
                    id="aircraftType" 
                    name="aircraftType"
                    className="form-control"
                    value={formData.aircraftType}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="squadron">Squadron:</label>
                  <input 
                    type="text" 
                    id="squadron" 
                    name="squadron"
                    className="form-control"
                    value={formData.squadron}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="group">Group:</label>
                  <input 
                    type="text" 
                    id="group" 
                    name="group"
                    className="form-control"
                    value={formData.group}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="wing">Wing:</label>
                  <input 
                    type="text" 
                    id="wing" 
                    name="wing"
                    className="form-control"
                    value={formData.wing}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="numberedAirForce">Numbered Air Force:</label>
                  <input 
                    type="text" 
                    id="numberedAirForce" 
                    name="numberedAirForce"
                    className="form-control"
                    value={formData.numberedAirForce}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="crewPosition">Crew Position:</label>
                  <input 
                    type="text" 
                    id="crewPosition" 
                    name="crewPosition"
                    className="form-control"
                    value={formData.crewPosition}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </>
        );
      case 'Civil Air Patrol (CAP)':
        return (
          <>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="squadron">Squadron:</label>
                  <input 
                    type="text" 
                    id="squadron" 
                    name="squadron"
                    className="form-control"
                    value={formData.squadron}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="group">Group:</label>
                  <input 
                    type="text" 
                    id="group" 
                    name="group"
                    className="form-control"
                    value={formData.group}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="wing">Wing:</label>
                  <input 
                    type="text" 
                    id="wing" 
                    name="wing"
                    className="form-control"
                    value={formData.wing}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="aircraftType">Aircraft Type:</label>
                  <input 
                    type="text" 
                    id="aircraftType" 
                    name="aircraftType"
                    className="form-control"
                    value={formData.aircraftType}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const renderLinealDescendantSection = () => {
    const sections = [];
    
    // Add explanation based on selected generation
    sections.push(
      <div key="generation-explanation" className="generation-explanation">
        <p>Based on your selection as a <strong>{formData.generation}</strong> descendant, 
        please provide information for the following generations:</p>
      </div>
    );
    
    // First Generation is always included
    sections.push(
      <div key="first-generation" className="generation-section">
        <h4>First Generation (Son/Daughter of Qualifying Veteran)</h4>
        {firstGeneration.map((descendant, index) => (
          <div key={descendant.id} className="descendant-entry">
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label>Name/Maiden Name:*</label>
                  <input 
                    type="text" 
                    className="form-control"
                    value={descendant.name}
                    onChange={(e) => updateLinealDescendant('first', descendant.id, 'name', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label>Date of Birth:*</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={descendant.dateOfBirth}
                    onChange={(e) => updateLinealDescendant('first', descendant.id, 'dateOfBirth', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label>Place of Birth:*</label>
                  <input 
                    type="text" 
                    className="form-control"
                    value={descendant.placeOfBirth}
                    onChange={(e) => updateLinealDescendant('first', descendant.id, 'placeOfBirth', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label>Spouse Name:</label>
                  <input 
                    type="text" 
                    className="form-control"
                    value={descendant.spouseName}
                    onChange={(e) => updateLinealDescendant('first', descendant.id, 'spouseName', e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label>Date of Death:</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={descendant.dateOfDeath}
                    onChange={(e) => updateLinealDescendant('first', descendant.id, 'dateOfDeath', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label>Place of Death:</label>
                  <input 
                    type="text" 
                    className="form-control"
                    value={descendant.placeOfDeath}
                    onChange={(e) => updateLinealDescendant('first', descendant.id, 'placeOfDeath', e.target.value)}
                  />
                </div>
              </div>
            </div>
            {index !== 0 && (
              <div className="form-row">
                <button 
                  type="button" 
                  className="btn remove-btn"
                  onClick={() => setFirstGeneration(firstGeneration.filter(d => d.id !== descendant.id))}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        ))}
        {firstGeneration.length < 4 && (
          <button 
            type="button" 
            className="btn add-btn"
            onClick={() => addLinealDescendant('first')}
          >
            Add First Generation Member
          </button>
        )}
      </div>
    );
    
    // Include Second Generation if needed
    if (formData.generation === 'Second Generation' || 
        formData.generation === 'Third Generation' || 
        formData.generation === 'Fourth Generation') {
      sections.push(
        <div key="second-generation" className="generation-section">
          <h4>Second Generation (Grandchild of Qualifying Veteran)</h4>
          {secondGeneration.length === 0 ? (
            <button 
              type="button" 
              className="btn add-btn"
              onClick={() => addLinealDescendant('second')}
            >
              Add Second Generation Member
            </button>
          ) : (
            <>
              {secondGeneration.map((descendant, index) => (
                <div key={descendant.id} className="descendant-entry">
                  <div className="form-row">
                    <div className="form-col">
                      <div className="form-group">
                        <label>Name/Maiden Name:</label>
                        <input 
                          type="text" 
                          className="form-control"
                          value={descendant.name}
                          onChange={(e) => updateLinealDescendant('second', descendant.id, 'name', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-col">
                      <div className="form-group">
                        <label>Date of Birth:</label>
                        <input 
                          type="date" 
                          className="form-control"
                          value={descendant.dateOfBirth}
                          onChange={(e) => updateLinealDescendant('second', descendant.id, 'dateOfBirth', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-col">
                      <div className="form-group">
                        <label>Place of Birth:</label>
                        <input 
                          type="text" 
                          className="form-control"
                          value={descendant.placeOfBirth}
                          onChange={(e) => updateLinealDescendant('second', descendant.id, 'placeOfBirth', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-col">
                      <div className="form-group">
                        <label>Spouse Name:</label>
                        <input 
                          type="text" 
                          className="form-control"
                          value={descendant.spouseName}
                          onChange={(e) => updateLinealDescendant('second', descendant.id, 'spouseName', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-col">
                      <div className="form-group">
                        <label>Date of Death:</label>
                        <input 
                          type="date" 
                          className="form-control"
                          value={descendant.dateOfDeath}
                          onChange={(e) => updateLinealDescendant('second', descendant.id, 'dateOfDeath', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="form-col">
                      <div className="form-group">
                        <label>Place of Death:</label>
                        <input 
                          type="text" 
                          className="form-control"
                          value={descendant.placeOfDeath}
                          onChange={(e) => updateLinealDescendant('second', descendant.id, 'placeOfDeath', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <button 
                      type="button" 
                      className="btn remove-btn"
                      onClick={() => setSecondGeneration(secondGeneration.filter(d => d.id !== descendant.id))}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              {secondGeneration.length < 4 && (
                <button 
                  type="button" 
                  className="btn add-btn"
                  onClick={() => addLinealDescendant('second')}
                >
                  Add Second Generation Member
                </button>
              )}
            </>
          )}
        </div>
      );
    }
    
    // Include Third Generation if needed
    if (formData.generation === 'Third Generation' || 
        formData.generation === 'Fourth Generation') {
      sections.push(
        <div key="third-generation" className="generation-section">
          <h4>Third Generation (Great-Grandchild of Qualifying Veteran)</h4>
          {thirdGeneration.length === 0 ? (
            <button 
              type="button" 
              className="btn add-btn"
              onClick={() => addLinealDescendant('third')}
            >
              Add Third Generation Member
            </button>
          ) : (
            <>
              {thirdGeneration.map((descendant, index) => (
                <div key={descendant.id} className="descendant-entry">
                  <div className="form-row">
                    <div className="form-col">
                      <div className="form-group">
                        <label>Name/Maiden Name:</label>
                        <input 
                          type="text" 
                          className="form-control"
                          value={descendant.name}
                          onChange={(e) => updateLinealDescendant('third', descendant.id, 'name', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-col">
                      <div className="form-group">
                        <label>Date of Birth:</label>
                        <input 
                          type="date" 
                          className="form-control"
                          value={descendant.dateOfBirth}
                          onChange={(e) => updateLinealDescendant('third', descendant.id, 'dateOfBirth', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-col">
                      <div className="form-group">
                        <label>Place of Birth:</label>
                        <input 
                          type="text" 
                          className="form-control"
                          value={descendant.placeOfBirth}
                          onChange={(e) => updateLinealDescendant('third', descendant.id, 'placeOfBirth', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-col">
                      <div className="form-group">
                        <label>Spouse Name:</label>
                        <input 
                          type="text" 
                          className="form-control"
                          value={descendant.spouseName}
                          onChange={(e) => updateLinealDescendant('third', descendant.id, 'spouseName', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-col">
                      <div className="form-group">
                        <label>Date of Death:</label>
                        <input 
                          type="date" 
                          className="form-control"
                          value={descendant.dateOfDeath}
                          onChange={(e) => updateLinealDescendant('third', descendant.id, 'dateOfDeath', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="form-col">
                      <div className="form-group">
                        <label>Place of Death:</label>
                        <input 
                          type="text" 
                          className="form-control"
                          value={descendant.placeOfDeath}
                          onChange={(e) => updateLinealDescendant('third', descendant.id, 'placeOfDeath', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <button 
                      type="button" 
                      className="btn remove-btn"
                      onClick={() => setThirdGeneration(thirdGeneration.filter(d => d.id !== descendant.id))}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              {thirdGeneration.length < 4 && (
                <button 
                  type="button" 
                  className="btn add-btn"
                  onClick={() => addLinealDescendant('third')}
                >
                  Add Third Generation Member
                </button>
              )}
            </>
          )}
        </div>
      );
    }
    
    // Include Fourth Generation if needed
    if (formData.generation === 'Fourth Generation') {
      sections.push(
        <div key="fourth-generation" className="generation-section">
          <h4>Fourth Generation (Great-Great-Grandchild of Qualifying Veteran)</h4>
          {fourthGeneration.length === 0 ? (
            <button 
              type="button" 
              className="btn add-btn"
              onClick={() => addLinealDescendant('fourth')}
            >
              Add Fourth Generation Member
            </button>
          ) : (
            <>
              {fourthGeneration.map((descendant, index) => (
                <div key={descendant.id} className="descendant-entry">
                  <div className="form-row">
                    <div className="form-col">
                      <div className="form-group">
                        <label>Name/Maiden Name:</label>
                        <input 
                          type="text" 
                          className="form-control"
                          value={descendant.name}
                          onChange={(e) => updateLinealDescendant('fourth', descendant.id, 'name', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-col">
                      <div className="form-group">
                        <label>Date of Birth:</label>
                        <input 
                          type="date" 
                          className="form-control"
                          value={descendant.dateOfBirth}
                          onChange={(e) => updateLinealDescendant('fourth', descendant.id, 'dateOfBirth', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-col">
                      <div className="form-group">
                        <label>Place of Birth:</label>
                        <input 
                          type="text" 
                          className="form-control"
                          value={descendant.placeOfBirth}
                          onChange={(e) => updateLinealDescendant('fourth', descendant.id, 'placeOfBirth', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-col">
                      <div className="form-group">
                        <label>Spouse Name:</label>
                        <input 
                          type="text" 
                          className="form-control"
                          value={descendant.spouseName}
                          onChange={(e) => updateLinealDescendant('fourth', descendant.id, 'spouseName', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-col">
                      <div className="form-group">
                        <label>Date of Death:</label>
                        <input 
                          type="date" 
                          className="form-control"
                          value={descendant.dateOfDeath}
                          onChange={(e) => updateLinealDescendant('fourth', descendant.id, 'dateOfDeath', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="form-col">
                      <div className="form-group">
                        <label>Place of Death:</label>
                        <input 
                          type="text" 
                          className="form-control"
                          value={descendant.placeOfDeath}
                          onChange={(e) => updateLinealDescendant('fourth', descendant.id, 'placeOfDeath', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <button 
                      type="button" 
                      className="btn remove-btn"
                      onClick={() => setFourthGeneration(fourthGeneration.filter(d => d.id !== descendant.id))}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              {fourthGeneration.length < 4 && (
                <button 
                  type="button" 
                  className="btn add-btn"
                  onClick={() => addLinealDescendant('fourth')}
                >
                  Add Fourth Generation Member
                </button>
              )}
            </>
          )}
        </div>
      );
    }
    
    return sections;
  };

  const renderSupportingDocuments = () => {
    return (
      <div className="form-section">
        <h3>Supporting Documentation (Attachments)</h3>
        
        <div className="upload-limit-note">
          <strong>File Size Limits:</strong> Each file must be under 10MB, and total upload size must be under 100MB.
          For best results, please compress your images before uploading.
          
          <div className="total-size-display">
            Current total size: <strong>{formatFileSize(totalFileSize)}</strong> of 100MB used
            <div className="total-size-progress">
              <div 
                className={`total-size-bar ${
                  totalFileSize > MAX_TOTAL_SIZE * 0.8 ? 'danger' : 
                  totalFileSize > MAX_TOTAL_SIZE * 0.5 ? 'warning' : ''
                }`} 
                style={{ width: `${Math.min(100, (totalFileSize / MAX_TOTAL_SIZE) * 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
        
         {/* Proof of Lineal Descent */}
        <div className="document-section">
          <h4 className="document-section-title">Proof of Lineal Descent:</h4>
          <div className="document-requirement">Min 1 attachment</div>
          {validationErrors.proofOfDescent && (
            <div className="validation-error">{validationErrors.proofOfDescent}</div>
          )}
          
          <div className="document-options">
            <div 
              className={`document-option clickable-document ${getFileList('birthCertificates').length > 0 ? 'document-uploaded' : ''}`}
              onClick={() => document.getElementById('birthCertificates').click()}
            >
              <input 
                type="file" 
                id="birthCertificates" 
                name="birthCertificates"
                onChange={handleFileChange}
                multiple
                className="hidden-file-input"
                style={{ display: 'none' }}
              />
              <h4>Birth Certificates</h4>
              <p>Official records confirming birth details and parentage to establish lineage</p>
              {getFileList('birthCertificates').length > 0 && (
                <div className="file-selected">
                  <p>Selected Files:</p>
                  <ul className="file-list">
                    {getFileList('birthCertificates').map((file, index) => (
                      <li key={index} className="file-item">
                        <span>{file.name}</span>
                        <button 
                          type="button" 
                          className="file-remove-btn" 
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile('birthCertificates', index);
                          }}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <div 
              className={`document-option clickable-document ${getFileList('marriageRecords').length > 0 ? 'document-uploaded' : ''}`}
              onClick={() => document.getElementById('marriageRecords').click()}
            >
              <input 
                type="file" 
                id="marriageRecords" 
                name="marriageRecords"
                onChange={handleFileChange}
                multiple
                className="hidden-file-input"
                style={{ display: 'none' }}
              />
              <h4>Marriage Records</h4>
              <p>Legal documents verifying marriages and name changes for family connections</p>
              {getFileList('marriageRecords').length > 0 && (
                <div className="file-selected">
                  <p>Selected Files:</p>
                  <ul className="file-list">
                    {getFileList('marriageRecords').map((file, index) => (
                      <li key={index} className="file-item">
                        <span>{file.name}</span>
                        <button 
                          type="button" 
                          className="file-remove-btn" 
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile('marriageRecords', index);
                          }}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
            </div>
            
        {/* Proof of Veteran's Service */}
        <div className="document-section">
          <h4 className="document-section-title">Proof of Veteran's Service:</h4>
          <div className="document-requirement">Min 1 attachment</div>
          {validationErrors.proofOfService && (
            <div className="validation-error">{validationErrors.proofOfService}</div>
          )}
          
          <div className="document-options">
            <div 
              className={`document-option clickable-document ${getFileList('dd214').length > 0 ? 'document-uploaded' : ''}`}
              onClick={() => document.getElementById('dd214').click()}
            >
              <input 
                type="file" 
                id="dd214" 
                name="dd214"
                onChange={handleFileChange}
                multiple
                className="hidden-file-input"
                style={{ display: 'none' }}
              />
              <h4>Enlistment / Discharge Records</h4>
              <p>Official military documents confirming a veteran's entry into and separation from service</p>
              {getFileList('dd214').length > 0 && (
                <div className="file-selected">
                  <p>Selected Files:</p>
                  <ul className="file-list">
                    {getFileList('dd214').map((file, index) => (
                      <li key={index} className="file-item">
                        <span>{file.name}</span>
                        <button 
                          type="button" 
                          className="file-remove-btn" 
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile('dd214', index);
                          }}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <div 
              className={`document-option clickable-document ${getFileList('militaryRecords').length > 0 ? 'document-uploaded' : ''}`}
              onClick={() => document.getElementById('militaryRecords').click()}
            >
              <input 
                type="file" 
                id="militaryRecords" 
                name="militaryRecords"
                onChange={handleFileChange}
                multiple
                className="hidden-file-input"
                style={{ display: 'none' }}
              />
              <h4>Military Records</h4>
              <p>Service-related documents such as unit assignments, medical records, etc.</p>
              {getFileList('militaryRecords').length > 0 && (
                <div className="file-selected">
                  <p>Selected Files:</p>
                  <ul className="file-list">
                    {getFileList('militaryRecords').map((file, index) => (
                      <li key={index} className="file-item">
                        <span>{file.name}</span>
                        <button 
                          type="button" 
                          className="file-remove-btn" 
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile('militaryRecords', index);
                          }}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
        </div>
        
            <div 
              className={`document-option clickable-document ${getFileList('vaPaperwork').length > 0 ? 'document-uploaded' : ''}`}
              onClick={() => document.getElementById('vaPaperwork').click()}
            >
              <input 
                type="file" 
                id="vaPaperwork" 
                name="vaPaperwork"
                onChange={handleFileChange}
                multiple
                className="hidden-file-input"
                style={{ display: 'none' }}
              />
              <h4>VA Paperwork</h4>
              <p>VA documents for benefits, disability claims, and service verification</p>
              {getFileList('vaPaperwork').length > 0 && (
                <div className="file-selected">
                  <p>Selected Files:</p>
                  <ul className="file-list">
                    {getFileList('vaPaperwork').map((file, index) => (
                      <li key={index} className="file-item">
                        <span>{file.name}</span>
                        <button 
                          type="button" 
                          className="file-remove-btn" 
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile('vaPaperwork', index);
                          }}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <div 
              className={`document-option clickable-document ${getFileList('pensionRecords').length > 0 ? 'document-uploaded' : ''}`}
              onClick={() => document.getElementById('pensionRecords').click()}
            >
              <input 
                type="file" 
                id="pensionRecords" 
                name="pensionRecords"
                onChange={handleFileChange}
                multiple
                className="hidden-file-input"
                style={{ display: 'none' }}
              />
              <h4>Pension Records</h4>
              <p>Military pension records confirming service and benefit eligibility</p>
              {getFileList('pensionRecords').length > 0 && (
                <div className="file-selected">
                  <p>Selected Files:</p>
                  <ul className="file-list">
                    {getFileList('pensionRecords').map((file, index) => (
                      <li key={index} className="file-item">
                        <span>{file.name}</span>
                        <button 
                          type="button" 
                          className="file-remove-btn" 
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile('pensionRecords', index);
                          }}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Photograph of Qualifying Veteran */}
        <div className="document-section">
          <h4 className="document-section-title">Photograph of Qualifying Veteran:</h4>
          <div className="document-requirement">Optional - Will be showcased on our website</div>
          
          <div className="document-options">
            <div 
              className={`document-option clickable-document ${getFileList('veteranPhoto').length > 0 ? 'document-uploaded' : ''}`}
              onClick={() => document.getElementById('veteranPhoto').click()}
            >
              <input 
                type="file" 
                id="veteranPhoto" 
                name="veteranPhoto"
                onChange={handleFileChange}
                multiple
                accept="image/*,application/pdf"
                className="hidden-file-input"
                style={{ display: 'none' }}
              />
              <h4>Veteran Photograph</h4>
              <p>A photo of the veteran during their time of service</p>
              {getFileList('veteranPhoto').length > 0 && (
                <div className="file-selected">
                  <p>Selected Files:</p>
                  <ul className="file-list">
                    {getFileList('veteranPhoto').map((file, index) => (
                      <li key={index} className="file-item">
                        <span>{file.name}</span>
                        <button 
                          type="button" 
                          className="file-remove-btn" 
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile('veteranPhoto', index);
                          }}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Miscellaneous */}
        <div className="document-section">
          <h4 className="document-section-title">Miscellaneous:</h4>
          <div className="document-requirement">Optional</div>
          
          <div className="document-options">
            <div 
              className={`document-option clickable-document ${getFileList('miscellaneous').length > 0 ? 'document-uploaded' : ''}`}
              onClick={() => document.getElementById('miscellaneous').click()}
            >
              <input 
                type="file" 
                id="miscellaneous" 
                name="miscellaneous"
                onChange={handleFileChange}
                multiple
                className="hidden-file-input"
                style={{ display: 'none' }}
              />
              <h4>Miscellaneous</h4>
              <p>Additional documents or photographs related to the veteran's service or family history</p>
              {getFileList('miscellaneous').length > 0 && (
                <div className="file-selected">
                  <p>Selected Files:</p>
                  <ul className="file-list">
                    {getFileList('miscellaneous').map((file, index) => (
                      <li key={index} className="file-item">
                        <span>{file.name}</span>
                        <button 
                          type="button" 
                          className="file-remove-btn" 
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile('miscellaneous', index);
                          }}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Validation functions
  const validateEmail = (email) => {
    // Regular expression for email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Handle reCAPTCHA verification
  const handleRecaptchaChange = (token) => {
    setRecaptchaVerified(!!token);
    setRecaptchaToken(token || '');
    
    if (token) {
      // If reCAPTCHA was successful, continue with form submission
      submitForm();
    } else {
      setValidationErrors(prev => ({
        ...prev,
        recaptcha: 'reCAPTCHA verification failed. Please try again.'
      }));
    }
  };

  // Function to handle the actual form submission after reCAPTCHA verification
  const submitForm = async () => {
    try {
      // Structure the data correctly for the API
      const apiSubmissionData = {
        formType: 'lineal',
        primaryApplicant: {
          fullName: formData.fullName,
          dateOfBirth: formData.applicantDateOfBirth,
          placeOfBirth: formData.applicantPlaceOfBirth,
          mailingAddress: {
            streetAddress: formData.streetAddress,
            city: formData.city,
            stateProvince: formData.stateProvince,
            postalCode: formData.postalCode,
            country: formData.country
          },
          telephone: formData.telephone,
          email: formData.email,
          generation: formData.generation
        },
        qualifyingVeteran: {
          name: formData.veteranName,
          dateOfBirth: formData.dateOfBirth,
          placeOfBirth: formData.placeOfBirth,
          dateOfDeath: formData.dateOfDeath,
          placeOfDeath: formData.placeOfDeath,
          serviceDetails: {
            datesOfService: formData.datesOfService,
            enlistmentDate: formData.enlistmentDate,
            enlistmentLocation: formData.enlistmentLocation,
            serviceNumber: formData.serviceNumber,
            branchOfService: formData.branchOfService,
            division: formData.division,
            regiment: formData.regiment,
            battalion: formData.battalion,
            company: formData.company,
            rank: formData.rank,
            theater: formData.theater,
            battles: formData.battles,
            awards: formData.awards,
            // Add branch-specific fields 
            shipName: formData.shipName,
            fleetAssignment: formData.fleetAssignment,
            battleGroup: formData.battleGroup,
            aircraftType: formData.aircraftType,
            squadron: formData.squadron,
            group: formData.group,
            wing: formData.wing,
            numberedAirForce: formData.numberedAirForce,
            crewPosition: formData.crewPosition,
            convoyAssignment: formData.convoyAssignment
          },
          additionalInfo: formData.additionalInfo,
          marriageDetails: {
            marriageStatus: formData.marriageStatus,
            marriageDate: formData.marriageDate,
            marriagePlace: formData.marriagePlace,
            spouseName: formData.spouseName,
            spouseBirthDate: formData.spouseBirthDate,
            spouseBirthPlace: formData.spouseBirthPlace,
            spouseDeathDate: formData.spouseDeathDate,
            spouseDeathPlace: formData.spouseDeathPlace
          }
        },
        // CRITICAL FIX: Pass original File objects, not converted plain objects
        // The upload service will handle the conversion internally if needed
        documentFiles: formData.documentFiles,
        // COMMENTED OUT: Lineal descendant data since verification section is disabled
        // linealDescendants: {
        //   firstGeneration: firstGeneration,
        //   secondGeneration: secondGeneration,
        //   thirdGeneration: thirdGeneration,
        //   fourthGeneration: fourthGeneration
        // },
        supplementalMembers: supplementalMembers,
        // Add consent fields
        consent: {
          acknowledgment: formData.acknowledgment,
          marketingOptIn: formData.marketingOptIn
        },
        // Add promo code and total amount
        payment: {
          promoCode: formData.promoCode,
          totalAmount: promoDiscount.value > 0 
            ? (promoDiscount.type === 'percentage' 
              ? ((250 + (supplementalMembers.length * 50)) * (1 - promoDiscount.value))
              : promoDiscount.type === 'mainApplication'
              ? (0 + (supplementalMembers.length * 50))
              : Math.max(0, (250 + (supplementalMembers.length * 50)) - promoDiscount.value))
            : (250 + (supplementalMembers.length * 50))
        },
        // Add how you heard about us
        howDidYouHearAboutUs: formData.howDidYouHearAboutUs,
        // Add reCAPTCHA token
        recaptchaToken: recaptchaToken
      };
      
      // Submit the form - the ApplicationForm component will handle file uploads
      await onSubmit(apiSubmissionData);
      
    } catch (error) {
      console.error('Error preparing form data for submission:', error);
      alert(`Error preparing form for submission: ${error.message}`);
    }
  };

  return (
    <form className="membership-form" onSubmit={handleSubmit}>
      <style jsx="true">{`
        .checkbox-container {
          display: flex;
          margin-bottom: 15px;
          align-items: flex-start;
        }
        
        .checkbox-container input[type="checkbox"] {
          margin-top: 4px;
          margin-right: 10px;
          flex-shrink: 0;
        }
        
        .checkbox-label {
          font-size: 1rem;
          line-height: 1.5;
        }
        
        .checkbox-label p {
          margin-top: 5px;
          font-size: 0.9rem;
          color: #555;
        }
      `}</style>

      <div className="form-header">
        <h2 className="form-title">Lineal Descendant Membership Application</h2>
        <p className="required-fields-note">Required fields are indicated by an asterisk (*)</p>
      </div>
      
      {/* Primary Applicant Information */}
      <div className="form-section">
        <h3>Primary Applicant Information</h3>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="fullName">Full Name:*</label>
              <input 
                type="text" 
                id="fullName" 
                name="fullName"
                className="form-control"
                value={formData.fullName}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="streetAddress">Street Address:*</label>
              <input 
                type="text" 
                id="streetAddress" 
                name="streetAddress"
                className="form-control"
                value={formData.streetAddress}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="city">City:*</label>
              <input 
                type="text" 
                id="city" 
                name="city"
                className="form-control"
                value={formData.city}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="stateProvince">State / Province / Region:*</label>
              <input 
                type="text" 
                id="stateProvince" 
                name="stateProvince"
                className="form-control"
                value={formData.stateProvince}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="postalCode">Postal Code / ZIP Code:*</label>
              <input 
                type="text" 
                id="postalCode" 
                name="postalCode"
                className="form-control"
                value={formData.postalCode}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="country">Country:*</label>
              <select 
                id="country" 
                name="country"
                className="form-control"
                value={formData.country}
                onChange={handleInputChange}
                required
              >
                {countries.map(country => (
                  <option key={country.code} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="applicantDateOfBirth">Date of Birth:*</label>
              <input 
                type="date" 
                id="applicantDateOfBirth" 
                name="applicantDateOfBirth"
                className="form-control"
                value={formData.applicantDateOfBirth}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="applicantPlaceOfBirth">Place of Birth:*</label>
              <input 
                type="text" 
                id="applicantPlaceOfBirth" 
                name="applicantPlaceOfBirth"
                className="form-control"
                value={formData.applicantPlaceOfBirth}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="telephone">Telephone:*</label>
              <input 
                type="tel" 
                id="telephone" 
                name="telephone"
                className="form-control"
                value={formData.telephone}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="email">Email:*</label>
              <input 
                type="email" 
                id="email" 
                name="email"
                className={`form-control ${validationErrors.email ? 'is-invalid' : ''}`}
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              {validationErrors.email && (
                <div className="validation-error">{validationErrors.email}</div>
              )}
            </div>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="generation">Generation:*</label>
              <select 
                id="generation" 
                name="generation"
                className="form-control"
                value={formData.generation}
                onChange={handleInputChange}
              >
                <option value="First Generation">First Generation</option>
                <option value="Second Generation">Second Generation</option>
                <option value="Third Generation">Third Generation</option>
                <option value="Fourth Generation">Fourth Generation</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      {/* Qualifying Veteran Information */}
      <div className="form-section">
        <h3>Qualifying Veteran Information</h3>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="veteranName">Full Name:*</label>
              <input 
                type="text" 
                id="veteranName" 
                name="veteranName"
                className="form-control"
                value={formData.veteranName}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="dateOfBirth">Date of Birth:*</label>
              <input 
                type="date" 
                id="dateOfBirth" 
                name="dateOfBirth"
                className="form-control"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="placeOfBirth">Place of Birth:</label>
              <input 
                type="text" 
                id="placeOfBirth" 
                name="placeOfBirth"
                className="form-control"
                value={formData.placeOfBirth}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="dateOfDeath">Date of Death:</label>
              <input 
                type="date" 
                id="dateOfDeath" 
                name="dateOfDeath"
                className="form-control"
                value={formData.dateOfDeath}
                onChange={handleInputChange}
              />
            </div>
          </div>
          
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="placeOfDeath">Place of Death:</label>
              <input 
                type="text" 
                id="placeOfDeath" 
                name="placeOfDeath"
                className="form-control"
                value={formData.placeOfDeath}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="datesOfService">Dates of Military Service:</label>
              <input 
                type="text" 
                id="datesOfService" 
                name="datesOfService"
                className="form-control"
                placeholder="Enter service dates in any format (e.g., 1942-1945, Dec 1941 - Aug 1943, etc.)"
                value={formData.datesOfService}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="enlistmentDate">Enlistment Date:</label>
              <input 
                type="date" 
                id="enlistmentDate" 
                name="enlistmentDate"
                className="form-control"
                value={formData.enlistmentDate}
                onChange={handleInputChange}
              />
            </div>
          </div>
          
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="enlistmentLocation">Enlistment Location:</label>
              <input 
                type="text" 
                id="enlistmentLocation" 
                name="enlistmentLocation"
                className="form-control"
                value={formData.enlistmentLocation}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="serviceNumber">Service Serial Number:</label>
              <input 
                type="text" 
                id="serviceNumber" 
                name="serviceNumber"
                className="form-control"
                value={formData.serviceNumber}
                onChange={handleInputChange}
              />
            </div>
          </div>
          
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="branchOfService">Branch of Service:*</label>
              <select 
                id="branchOfService" 
                name="branchOfService"
                className={`form-control ${validationErrors.branchOfService ? 'is-invalid' : ''}`}
                value={formData.branchOfService}
                onChange={handleInputChange}
                required
              >
                <option value="Select">Select</option>
                <option value="United States Army (USA)">United States Army (USA)</option>
                <option value="United States Navy (USN)">United States Navy (USN)</option>
                <option value="United States Marine Corps (USMC)">United States Marine Corps (USMC)</option>
                <option value="United States Coast Guard (USCG)">United States Coast Guard (USCG)</option>
                <option value="United States Merchant Marine (USMM)">United States Merchant Marine (USMM)</option>
                <option value="United States Army Air Forces (USAAF)">United States Army Air Forces (USAAF)</option>
                <option value="Civil Air Patrol (CAP)">Civil Air Patrol (CAP)</option>
              </select>
              {validationErrors.branchOfService && (
                <div className="validation-error">{validationErrors.branchOfService}</div>
              )}
            </div>
          </div>
        </div>

        {/* Branch-specific fields rendered here */}
        {renderBranchSpecificFields()}

        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="rank">Rank or Rating at Separation:</label>
              <input 
                type="text" 
                id="rank" 
                name="rank"
                className="form-control"
                value={formData.rank}
                onChange={handleInputChange}
              />
            </div>
          </div>
          
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="theater">Theater:</label>
              <select 
                id="theater" 
                name="theater"
                className={`form-control ${validationErrors.theater ? 'is-invalid' : ''}`}
                value={formData.theater}
                onChange={handleInputChange}
              >
                <option value="Select">Select</option>
                <option value="European Theater">European Theater</option>
                <option value="Pacific Theater">Pacific Theater</option>
                <option value="Mediterranean Theater">Mediterranean Theater</option>
                <option value="China-Burma-India Theater">China-Burma-India Theater</option>
                <option value="American Theater">American Theater</option>
                <option value="Multiple Theaters">Multiple Theaters</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="battles">Battles/Campaigns Participated In:</label>
          <textarea 
            id="battles" 
            name="battles"
            className="form-control"
            rows="3"
            placeholder="Please list any battles or campaigns the veteran participated in."
            value={formData.battles}
            onChange={handleInputChange}
          ></textarea>
        </div>
        
        <div className="form-group">
          <label htmlFor="awards">Awards & Decorations:</label>
          <textarea 
            id="awards" 
            name="awards"
            className="form-control"
            rows="3"
            placeholder="Please list any awards, medals, or decorations the veteran received."
            value={formData.awards}
            onChange={handleInputChange}
          ></textarea>
        </div>
        
        <div className="form-group">
          <label htmlFor="additionalInfo">Additional Service Information:</label>
          <textarea 
            id="additionalInfo" 
            name="additionalInfo"
            className="form-control"
            rows="4"
            value={formData.additionalInfo}
            onChange={handleInputChange}
          ></textarea>
        </div>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="marriageStatus">Marriage Status:*</label>
              <select 
                id="marriageStatus" 
                name="marriageStatus"
                className="form-control"
                value={formData.marriageStatus}
                onChange={handleInputChange}
                required
              >
                <option value="Select">Select</option>
                <option value="Married">Married</option>
                <option value="Single/Never Married">Single/Never Married</option>
                <option value="Divorced">Divorced</option>
              </select>
            </div>
          </div>
        </div>
        
        {(formData.marriageStatus === 'Married' || formData.marriageStatus === 'Divorced') && (
          <>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="marriageDate">Marriage Date:</label>
                  <input 
                    type="date" 
                    id="marriageDate" 
                    name="marriageDate"
                    className="form-control"
                    value={formData.marriageDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="marriagePlace">Marriage Place:</label>
                  <input 
                    type="text" 
                    id="marriagePlace" 
                    name="marriagePlace"
                    className="form-control"
                    value={formData.marriagePlace}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="spouseName">Spouse Name:</label>
                  <input 
                    type="text" 
                    id="spouseName" 
                    name="spouseName"
                    className="form-control"
                    value={formData.spouseName}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="spouseBirthDate">Spouse Birth Date:</label>
                  <input 
                    type="date" 
                    id="spouseBirthDate" 
                    name="spouseBirthDate"
                    className="form-control"
                    value={formData.spouseBirthDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="spouseBirthPlace">Spouse Birth Place:</label>
                  <input 
                    type="text" 
                    id="spouseBirthPlace" 
                    name="spouseBirthPlace"
                    className="form-control"
                    value={formData.spouseBirthPlace}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="spouseDeathDate">Spouse Death Date:</label>
                  <input 
                    type="date" 
                    id="spouseDeathDate" 
                    name="spouseDeathDate"
                    className="form-control"
                    value={formData.spouseDeathDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="spouseDeathPlace">Spouse Death Place:</label>
                  <input 
                    type="text" 
                    id="spouseDeathPlace" 
                    name="spouseDeathPlace"
                    className="form-control"
                    value={formData.spouseDeathPlace}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Lineal Descendant Information */}
      {/* COMMENTED OUT: Lineal Descendant Verification Section
      <div className="form-section">
        <h3>Lineal Descendant Verification</h3>
        {renderLinealDescendantSection()}
      </div>
      */}
      
      {/* Supplemental Family Members */}
      <div className="form-section">
        <h3>Supplemental Family Members (For additional relatives joining)</h3>
        <p className="section-info">Add additional members who are related to the veteran</p>
        
        {supplementalMembers.length > 0 ? (
          supplementalMembers.map((member, index) => (
            <div key={member.id} className="descendant-entry supplemental-entry">
              <h4>Supplemental Member {index + 1}</h4>
              <div className="form-row">
                <div className="form-col">
                  <div className="form-group">
                    <label>Applicant Name:*</label>
                    <input 
                      type="text" 
                      className="form-control"
                      value={member.name}
                      onChange={(e) => updateSupplementalMember(member.id, 'name', e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-col">
                  <div className="form-group">
                    <label>Date of Birth:*</label>
                    <input 
                      type="date" 
                      className="form-control"
                      value={member.dateOfBirth}
                      onChange={(e) => updateSupplementalMember(member.id, 'dateOfBirth', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-col">
                  <div className="form-group">
                    <label>Street Address:*</label>
                    <input 
                      type="text" 
                      className="form-control"
                      value={member.streetAddress}
                      onChange={(e) => updateSupplementalMember(member.id, 'streetAddress', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-col">
                  <div className="form-group">
                    <label>City:*</label>
                    <input 
                      type="text" 
                      className="form-control"
                      value={member.city}
                      onChange={(e) => updateSupplementalMember(member.id, 'city', e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-col">
                  <div className="form-group">
                    <label>State / Province / Region:*</label>
                    <input 
                      type="text" 
                      className="form-control"
                      value={member.stateProvince}
                      onChange={(e) => updateSupplementalMember(member.id, 'stateProvince', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-col">
                  <div className="form-group">
                    <label>Postal Code / ZIP Code:*</label>
                    <input 
                      type="text" 
                      className="form-control"
                      value={member.postalCode}
                      onChange={(e) => updateSupplementalMember(member.id, 'postalCode', e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-col">
                  <div className="form-group">
                    <label>Country:*</label>
                    <select
                      className="form-control"
                      value={member.country}
                      onChange={(e) => updateSupplementalMember(member.id, 'country', e.target.value)}
                      required
                    >
                      {countries.map(country => (
                        <option key={`${member.id}-${country.code}`} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-col">
                  <div className="form-group">
                    <label>Telephone:</label>
                    <input 
                      type="tel" 
                      className="form-control"
                      value={member.telephone}
                      onChange={(e) => updateSupplementalMember(member.id, 'telephone', e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="form-col">
                  <div className="form-group">
                    <label>Email:*</label>
                    <input 
                      type="email" 
                      className={`form-control ${validationErrors[`supplementalEmail${index}`] ? 'is-invalid' : ''}`}
                      value={member.email}
                      onChange={(e) => updateSupplementalMember(member.id, 'email', e.target.value)}
                      required
                    />
                    {validationErrors[`supplementalEmail${index}`] && (
                      <div className="validation-error">{validationErrors[`supplementalEmail${index}`]}</div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-col">
                  <div className="form-group">
                    <label>Relationship to Primary Applicant:*</label>
                    <input 
                      type="text" 
                      className="form-control"
                      value={member.relationship}
                      onChange={(e) => updateSupplementalMember(member.id, 'relationship', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <button 
                  type="button" 
                  className="btn remove-btn"
                  onClick={() => setSupplementalMembers(supplementalMembers.filter(m => m.id !== member.id))}
                >
                  Remove
                </button>
              </div>
            </div>
          ))
        ) : (
          <p>No supplemental members added yet.</p>
        )}
        
        {supplementalMembers.length < 20 && (
          <button 
            type="button" 
            className="btn add-btn"
            onClick={addSupplementalMember}
          >
            Add Supplemental Member
          </button>
        )}
      </div>
      
      {/* Supporting Documentation */}
      {renderSupportingDocuments()}
      
      {/* Calculated Total Due */}
      <div className="form-section">
        <h3>Total Due on Application Approval</h3>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="promoCode">Promo Code (if applicable):</label>
              <input 
                type="text" 
                id="promoCode" 
                name="promoCode"
                className="form-control"
                value={formData.promoCode}
                onChange={handleInputChange}
                placeholder="Enter promo code if you have one"
              />
            </div>
          </div>
        </div>
        
        <div className="fee-calculation">
          <ul className="fee-list">
            <li>Main Application = $250</li>
            <li>Any Supplemental Member = $50</li>
            {promoDiscount.value > 0 && promoDiscount.type === 'percentage' && (
              <li>Promo Discount: -{(promoDiscount.value * 100).toFixed(0)}%</li>
            )}
            {promoDiscount.value > 0 && promoDiscount.type === 'fixed' && (
              <li>Promo Discount: -${promoDiscount.value.toFixed(2)}</li>
            )}
            {promoDiscount.value > 0 && promoDiscount.type === 'mainApplication' && (
              <li>Promo Discount: 100% off main application fee</li>
            )}
          </ul>
          <div className="total-amount">
            {promoDiscount.value > 0 ? (
              <>
                <span className="original-price">${250 + (supplementalMembers.length * 50)}</span>
                <strong>
                  Total Due: ${promoDiscount.type === 'percentage' 
                    ? ((250 + (supplementalMembers.length * 50)) * (1 - promoDiscount.value)).toFixed(2)
                    : promoDiscount.type === 'mainApplication'
                    ? (0 + (supplementalMembers.length * 50)).toFixed(2)
                    : Math.max(0, (250 + (supplementalMembers.length * 50)) - promoDiscount.value).toFixed(2)}
                </strong>
              </>
            ) : (
              <strong>Total Due: ${250 + (supplementalMembers.length * 50)}</strong>
            )}
          </div>
        </div>
      </div>
      
      {/* Acknowledgment and Consent section */}
      <div className="form-section">
        <div className="form-group">
          <div className="checkbox-container">
            <input 
              type="checkbox" 
              id="acknowledgment" 
              name="acknowledgment"
              checked={formData.acknowledgment}
              onChange={handleInputChange}
              required
            />
            <label htmlFor="acknowledgment" className="checkbox-label">
              <strong>Acknowledgement</strong> - Required Field in order to submit application
              <p>This is a public database dedicated to honoring veterans. Please note that photographs submitted will be used in a publicly accessible format. No private or sensitive personal information that is collected will be shared—only images and basic identifying details (such as name, rank, and service branch, as applicable) will be included in the public display. By participating, you acknowledge and consent to the public use of these photographs for commemorative and educational purposes.</p>
            </label>
          </div>
          {validationErrors.acknowledgment && (
            <div className="validation-error">{validationErrors.acknowledgment}</div>
          )}
        </div>
        
        <div className="form-group">
          <div className="checkbox-container">
            <input 
              type="checkbox" 
              id="marketingOptIn" 
              name="marketingOptIn"
              checked={formData.marketingOptIn}
              onChange={handleInputChange}
            />
            <label htmlFor="marketingOptIn" className="checkbox-label">
              <strong>Marketing Opt-in (Optional)</strong>: Not required field
              <p>We would love to honor veterans by sharing their photos and basic information (such as name, rank, and service branch) on our official social media accounts. No private or sensitive personal information will be shared.</p>
            </label>
          </div>
        </div>
      </div>
      
      {/* Invisible reCAPTCHA */}
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI" // Replace with your actual site key in production
        size="invisible"
        onChange={handleRecaptchaChange}
      />
      {validationErrors.recaptcha && (
        <div className="validation-error recaptcha-error">{validationErrors.recaptcha}</div>
      )}
      
      {/* How did you hear about us */}
      <div className="form-section">
        <div className="form-group">
          <label htmlFor="howDidYouHearAboutUs"><strong>How Did You Hear About Us</strong></label>
          <textarea 
            id="howDidYouHearAboutUs" 
            name="howDidYouHearAboutUs"
            className="form-control"
            rows="3"
            placeholder="Please let us know how you discovered our organization..."
            value={formData.howDidYouHearAboutUs}
            onChange={handleInputChange}
          ></textarea>
        </div>
      </div>
      
      {/* Contact Information */}
      <div className="form-section">
        <h3>Contact Information</h3>
        <p>For more information, please contact Courtney Carr, WWII Veterans Legacy Program Manager at <a href="mailto:ccarr@nimitzfoundation.org">ccarr@nimitzfoundation.org</a> or 830-997-8600 EXT 220</p>
      </div>
      
      {/* Submit button with loading state */}
      <div className="form-section form-actions">
        <button 
          type="submit" 
          className="btn btn-primary" 
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Application'}
        </button>
        {isSubmitting && (
          <div className="loader-container">
            <div className="loader"></div>
            <p>Uploading files and submitting application. This may take a few moments...</p>
          </div>
        )}
      </div>
    </form>
  );
};

export default LinealForm; 